import { addMonths, format, isSameDay } from 'date-fns';
import { groupBy, some, sortBy, uniq, uniqBy } from 'lodash';
import { EventService } from 'src/event/service/event.service';


export const getFilteredCoaches = (coaches, filters) => {
  let filteredCoaches = [...coaches];
  if (filters?.coaches?.length > 0) {
    const selectedCoaches = filters?.coaches.map(Number);
    filteredCoaches = filteredCoaches.filter((coach) => {
      return selectedCoaches?.includes(coach?.id);
    });
  }

  if (filters?.tags?.length > 0) {
    const selectedTags = filters?.tags.map(Number);
    filteredCoaches = filteredCoaches.filter((coach) => {
      const categoryValues = coach?.categoryValues || [];
      const containAnyCategory = categoryValues.filter((categoryValue) => {
        return selectedTags.includes(Number(categoryValue?.categoryId));
      });
      return containAnyCategory.length > 0;
    });
  }
  if (filters?.categories?.length > 0) {
    const selectedCategories = filters?.categories.map((category) => {
      const [categoryId, categoryOptionId] = category?.split('_') || [];
      return {
        categoryId,
        categoryOptionId,
      };
    });
    const groupedByCategory = groupBy(selectedCategories, 'categoryId');
    filteredCoaches = filteredCoaches.filter((coach) => {
      const categoryValues = coach?.categoryValues || [];
      const containAllCategory = Object.keys(groupedByCategory).filter(
        (categoryId) => {
          const categoryOptions = new Set(
            groupedByCategory[categoryId].map((item) => item.categoryOptionId),
          );
          const containAnyCategory = categoryValues.filter((categoryValue) => {
            return (
              categoryValue?.categoryId === categoryId &&
              categoryOptions.has(categoryValue?.categoryOptionId)
            );
          });
          return containAnyCategory.length > 0;
        },
      );
      return (
        containAllCategory.length === Object.keys(groupedByCategory).length
      );
    });
  }
  return filteredCoaches;
};

export const COACH_ASSIGNMENT_ENUM = {
  ALL: 2,
  CURRENT_DATE: 1,
  SELECTED: 3,
};

const sortCoachItemsByConflicts = ({
  allowedConflicts,
  available,
  coachAssignmentOption,
  coachesConflicts,
  items,
  staffed,
  unavailable,
}) => {
  const sortCoachesByName = (coaches) => sortBy(coaches, ['fullName']);

  const allowedConflictsCount =
    coachAssignmentOption === COACH_ASSIGNMENT_ENUM.CURRENT_DATE
      ? 0
      : allowedConflicts;
  let availableCoaches = available.filter((coach) => {
    const coachConflicts = coachesConflicts[coach?.id];
    if (!coachConflicts?.id) {
      return true;
    }
    if (
      coachConflicts?.conflictingDates?.length > 0 &&
      coachConflicts?.conflictingDates?.length > allowedConflictsCount
    ) {
      staffed.push(coach);
      return false;
    }
    return true;
  });
  let unavailableCoaches = unavailable.filter((coach) => {
    const coachConflicts = coachesConflicts[coach?.id];
    if (!coachConflicts?.id) {
      return true;
    }
    if (
      coachConflicts?.conflictingDates?.length > 0 &&
      coachConflicts?.conflictingDates?.length > allowedConflictsCount
    ) {
      staffed.push(coach);
      return false;
    }
    return true;
  });
  let staffedCoaches = staffed.filter((coach) => {
    const coachConflicts = coachesConflicts[coach?.id];
    if (!coachConflicts?.id) {
      return true;
    }
    if (
      coachConflicts?.conflictingDates?.length > 0 &&
      coachConflicts?.conflictingDates?.length <= allowedConflictsCount
    ) {
      const coachItem = items.find((item) => item.coach?.id === coach.id);
      if (coachItem?.isCoachAvailable) {
        availableCoaches.push(coach);
      } else {
        unavailableCoaches.push(coach);
      }
      return false;
    }
    return true;
  });

  availableCoaches = sortCoachesByName(availableCoaches);
  staffedCoaches = sortCoachesByName(staffedCoaches).filter((coach) => {
    return !availableCoaches.some((item) => item.id === coach.id);
  });
  unavailableCoaches = sortCoachesByName(unavailableCoaches);
  const sortedItems = sortBy(items, [
    (item) => {
      const rank = availableCoaches.some((coach) => coach.id === item.coach?.id)
        ? 1
        : staffedCoaches.some((coach) => coach.id === item.coach?.id)
          ? 2
          : 3;

      return rank;
    },
    'fullName',
  ]);
  return {
    availableCoachIds: uniq(availableCoaches.map((coach) => coach?.id)),
    items: sortedItems,
    staffedCoachIds: uniq(staffedCoaches.map((coach) => coach?.id)),
    unavailableCoachIds: uniq(unavailableCoaches.map((coach) => coach?.id)),
  };
};

export const getCalendarItems = ({
  allowedConflicts,
  coachAssignmentOption,
  coaches,
  coachesConflicts,
  currentFranchiseId,
  eventDates,
  filters,
}) => {
  const classTimes = eventDates.map((eventDate) => {
    const eventDateStartDateTime = EventService.eventDateStartTime(eventDate);
    const eventDateEndDateTime = EventService.eventDateEndTime(eventDate);
    return {
      classEnd: eventDateEndDateTime,
      classStart: eventDateStartDateTime,
      edId: eventDate?.id,
      event: eventDate?.event,
      id: eventDate.id,
    };
  });

  const calendarItems = [];
  const availableCoaches = [];
  const staffedCoaches = [];
  const unavailableCoaches = [];
  const filteredCoaches = getFilteredCoaches(coaches, filters);

  for (const coach of filteredCoaches) {
    const eventDates = coach?.coachEventDates?.map((red) => {
      const eventDateStartDateTime = EventService.eventDateStartTime(red);
      const eventDateEndDateTime = EventService.eventDateEndTime(red);

      const eventDateInClassTimes = classTimes.some((classTime) => {
        return (
          eventDateStartDateTime < classTime.classEnd &&
          eventDateEndDateTime > classTime.classStart
        );
      });

      return {
        ...red,
        backgroundColor: '#dbe2f4',
        coach: { ...coach },
        endTime: eventDateEndDateTime,
        eventDateInClassTimes,
        eventType: 'staffed',
        startTime: eventDateStartDateTime,
        zIndex: 5,
      };
    });
    let coachAvailabilityStatus = 'available';

    let isCoachAvailable = false;
    const classAvailableHourList = [];
    const coachAllDayAvailability = [];
    classTimes.map((classTime) => {
      const { code, effectiveName, id: eId, label } = classTime?.event || {};
      const eventData = {
        code,
        eId,
        edId: Number(classTime?.edId),
        effectiveName,
        label,
      };
      const eventDatesInClassTimes = eventDates?.filter((eventDate) => {
        return eventDate.eventDateInClassTimes;
      });

      const assignedToCurrentEvent =
        eventDatesInClassTimes?.length > 0 &&
        eventDatesInClassTimes.some((eventDate) => {
          return eventDate?.id === classTime.id;
        });
      const assignedToAnotherEventInAnotherFranchise =
        eventDatesInClassTimes?.length > 0 &&
        eventDatesInClassTimes.some((eventDate) => {
          return (
            Number(eventDate?.event?.franchise?.id) !==
              Number(currentFranchiseId) || false
          );
        });

      if (coach?.coachAvailability?.length > 0) {
        for (const availability of coach?.coachAvailability || []) {
          if (!availability?.id) continue;

          const { date, endTime, startTime } = availability || {};
          const availabilityStart = new Date(
            `${date.split('T')[0]}T${startTime}`,
          );
          const availabilityEnd = new Date(`${date.split('T')[0]}T${endTime}`);
          const isCurrentAvailabilityInClassTimes = classTimes.every(
            (classTime) => {
              return (
                availabilityStart >= classTime.startTime &&
                availabilityEnd <= classTime.endTime
              );
            },
          );
          coachAllDayAvailability.push({
            backgroundColor: '#e2f4e2',
            coach: { ...coach },
            endTime: availabilityEnd,
            event: eventData,
            eventType: 'allDayAvailability',
            startTime: availabilityStart,
            zIndex: 1,
          });
          if (!isCurrentAvailabilityInClassTimes || assignedToCurrentEvent) {
            continue;
          }
          isCoachAvailable = true;
        }
      }

      if (
        !isCoachAvailable &&
        !assignedToCurrentEvent &&
        !assignedToAnotherEventInAnotherFranchise
      ) {
        classAvailableHourList.push({
          backgroundColor: '#c6c9c6',
          coach: { ...coach },
          endTime: classTime.classEnd,
          event: eventData,
          eventType: 'unavailable',
          startTime: classTime.classStart,
          zIndex: 5,
        });
        unavailableCoaches.push(coach);
        coachAvailabilityStatus = 'unavailable';
      }

      if (
        isCoachAvailable &&
        !assignedToCurrentEvent &&
        !assignedToAnotherEventInAnotherFranchise
      ) {
        classAvailableHourList.push({
          backgroundColor: '#e2f4e2',
          coach: { ...coach },
          endTime: classTime.classEnd,
          event: eventData,
          eventType: 'available',
          startTime: classTime.classStart,
          zIndex: 5,
        });

        availableCoaches.push(coach);
      }

      if (eventDatesInClassTimes?.length > 0) {
        staffedCoaches.push(coach);
        coachAvailabilityStatus = 'staffed';
      }
    });

    // event dates with exact same start and end time
    // then add 10 minutes to the end time
    for (const eventDate of eventDates) {
      const isStartAndEndAtSameTimeWithAnyOtherEventDate = eventDates.filter(
        (date) =>
          eventDate.startTime?.getTime() === date.startTime?.getTime() &&
          eventDate.endTime?.getTime() === date.endTime?.getTime() &&
          eventDate.id !== date.id,
      );

      if (
        !isStartAndEndAtSameTimeWithAnyOtherEventDate?.length ||
        eventDate.updatedEndTime
      ) {
        continue;
      }
      // TODO: in progress
      eventDate.endTime = new Date(eventDate.endTime.getTime() + 20 * 60000);
      eventDate.updatedEndTime = true;
    }

    calendarItems.push({
      coach: { ...coach },
      coachAvailabilityStatus,
      events: [
        ...eventDates,
        ...classAvailableHourList,
        ...coachAllDayAvailability,
      ],
      isCoachAvailable,
    });
  }
  return sortCoachItemsByConflicts({
    allowedConflicts,
    available: uniqBy(availableCoaches, 'id'),
    coachAssignmentOption,
    coachesConflicts,
    items: sortBy(calendarItems, (item) =>
      item?.coach?.fullName?.toLowerCase(),
    ),
    staffed: uniqBy(staffedCoaches, 'id'),
    unavailable: uniqBy(unavailableCoaches, 'id'),
  });
};

export const getCategoryOptions = (coaches, allCategories) => {
  const filterCategories = [];
  for (const entityCategory of allCategories || []) {
    const coachesWithCategory = coaches?.filter((coach) => {
      return (
        coach?.categoryValues?.filter((categoryValue) => {
          return categoryValue?.categoryId === entityCategory?.category?.id;
        }).length > 0
      );
    });
    if (coachesWithCategory?.length > 0) {
      filterCategories.push({
        ...entityCategory,
        category: {
          ...entityCategory?.category,
          categoryOptions: entityCategory?.category?.categoryOptions?.filter(
            (option) => {
              return (
                coachesWithCategory?.filter((coach) => {
                  return (
                    coach?.categoryValues?.filter((categoryValue) => {
                      return (
                        categoryValue?.categoryId ===
                          entityCategory?.category?.id &&
                        categoryValue?.categoryOptionId === option?.id
                      );
                    }).length > 0
                  );
                }).length > 0
              );
            },
          ),
        },
      });
    }
  }
  return filterCategories;
};

export const getCoachConflicts = ({
  coachAssignmentOption,
  coaches,
  conflicts,
  selectedDates,
  totalDatesCount,
}) => {
  if (
    !conflicts?.length ||
    !coaches?.length ||
    (coachAssignmentOption === COACH_ASSIGNMENT_ENUM.SELECTED &&
      !selectedDates?.length)
  ) {
    return [];
  }
  const coachesMap = {};

  for (const coach of coaches) {
    const coachId = Number(coach?.id);
    const totalDates =
      coachAssignmentOption === COACH_ASSIGNMENT_ENUM.ALL
        ? totalDatesCount
        : selectedDates.length;

    coachesMap[coachId] = {
      conflictingDates: [],
      id: coachId,
      totalDates,
    };
  }

  for (const conflict of conflicts) {
    if (coachAssignmentOption !== COACH_ASSIGNMENT_ENUM.ALL) {
      const conflictDateExists = some(selectedDates, (date) =>
        isSameDay(
          new Date(date?.date || date?.startDateTimeInUTC),
          new Date(conflict?.startDateTimeInUTC),
        ),
      );
      if (!conflictDateExists) {
        continue;
      }
    }

    for (const staffAssignment of conflict?.staffAssignments || []) {
      if (
        coachesMap[Number(staffAssignment?.id || staffAssignment?.assigneeId)]
      ) {
        coachesMap[
          Number(staffAssignment?.id || staffAssignment?.assigneeId)
        ].conflictingDates.push(conflict);
      }
    }
  }

  for (const coachId of Object.keys(coachesMap)) {
    coachesMap[coachId].conflictingDates = uniqBy(
      coachesMap[coachId].conflictingDates,
      'id',
    );
  }

  return coachesMap;
};
export const COACH_ASSIGNMENT_ACTION_ITEMS = [
  {
    id: COACH_ASSIGNMENT_ENUM.CURRENT_DATE,
    text: 'Apply to this session only.',
    value: COACH_ASSIGNMENT_ENUM.CURRENT_DATE,
  },
  {
    id: COACH_ASSIGNMENT_ENUM.ALL,
    text: 'Apply to this & following sessions.',
    value: COACH_ASSIGNMENT_ENUM.ALL,
  },
  {
    id: COACH_ASSIGNMENT_ENUM.SELECTED,
    text: 'Apply to selected sessions only.',
    value: COACH_ASSIGNMENT_ENUM.SELECTED,
  },
];
export const getCoachAssignmentInput = ({
  assignedStaff,
  coachAssignmentOption,
  datesState,
  eventDate,
  notifications,
  selectedDates,
  setEventDatesToHighlight,
}) => {
  const input = {
    coaches: assignedStaff,
    eventId: Number(eventDate?.event.id),
    notifications,
    startingEventDateId:
      coachAssignmentOption === COACH_ASSIGNMENT_ENUM.SELECTED
        ? null
        : +eventDate?.id,
  };
  switch (coachAssignmentOption) {
    case COACH_ASSIGNMENT_ENUM.CURRENT_DATE: {
      input.assignmentAction = 'CURRENT_DATE_ONLY';
      input.includedEventDateIds = [Number(eventDate.id)];
      setEventDatesToHighlight([
        format(
          typeof eventDate.start === 'string'
            ? new Date(eventDate.start)
            : eventDate.start,
          'yyyy-MM-dd',
        ),
      ]);
      input.coaches = input.coaches.map((c) => ({
        ...c,
        showCoachOnDetails: false,
      }));
      break;
    }
    case COACH_ASSIGNMENT_ENUM.ALL: {
      input.assignmentAction = 'CURRENT_AND_FOLLOWING';
      const dates = [];
      const startDateToHighlight = format(
        typeof eventDate.start === 'string'
          ? new Date(eventDate.start)
          : eventDate.start,
        'yyyy-MM-dd',
      );
      const endDateToHighlight = format(
        addMonths(
          typeof eventDate.start === 'string'
            ? new Date(eventDate.start)
            : eventDate.start,
          1,
        ),
        'yyyy-MM-dd',
      );
      datesState.map((item) => {
        if (
          item.date >= startDateToHighlight &&
          item.date <= endDateToHighlight
        ) {
          dates.push(item.date);
        }
      });
      setEventDatesToHighlight([...dates]);
      break;
    }
    case COACH_ASSIGNMENT_ENUM.SELECTED: {
      input.assignmentAction = 'SPECIFIC_DATES';
      input.includedEventDateIds = selectedDates.map(Number);
      input.coaches = input.coaches.map((c) => ({
        ...c,
        showCoachOnDetails: false,
      }));

      const dates = datesState.filter((date) =>
        selectedDates.includes(date.id),
      );
      setEventDatesToHighlight([...dates]);
      break;
    }
  }
  return input;
};
export const actionsOnConflict = [
  {
    label: 'Remove from conflicting classes',
    value: 'REMOVE_FROM_CONFLICTED_DATES',
  },
  {
    label: 'Skip conflicts',
    value: 'SKIP_IF_CONFLICTED',
  },
  {
    label: 'Double Staff',
    value: 'DOUBLE_STAFF_IF_CONFLICTED',
  },
];
